import { createContext, useContext, useState, useCallback } from 'react';

/**
 * Context for managing both repository data and repository requests
 */
const RepositoryContext = createContext();

/**
 * Provider component for repository management functionality
 * Provides refresh capabilities that any component can subscribe to
 */
export const RepositoryProvider = ({ children }) => {
  // Use separate counters to trigger refreshes for different data types
  const [repoRequestsTrigger, setRepoRequestsTrigger] = useState(0);
  const [approvedReposTrigger, setApprovedReposTrigger] = useState(0);

  /**
   * Function to refresh repository requests data (pending approvals)
   * Used when the requests table needs to be refreshed
   */
  const refreshRepositoryRequests = useCallback(() => {
    setRepoRequestsTrigger((prev) => prev + 1);
  }, []);

  /**
   * Function to refresh approved repositories data
   * Used when the approved repositories table needs to be refreshed
   */
  const refreshApprovedRepositories = useCallback(() => {
    setApprovedReposTrigger((prev) => prev + 1);
  }, []);

  /**
   * Function to refresh both repository requests and approved repositories
   * Used when an action affects both datasets (like when approving a request)
   */
  const refreshAllRepositoryData = useCallback(() => {
    refreshRepositoryRequests();
    refreshApprovedRepositories();
  }, [refreshRepositoryRequests, refreshApprovedRepositories]);

  return (
    <RepositoryContext.Provider
      value={{
        repoRequestsTrigger,
        approvedReposTrigger,
        refreshRepositoryRequests,
        refreshApprovedRepositories,
        refreshAllRepositoryData,
      }}
    >
      {children}
    </RepositoryContext.Provider>
  );
};

/**
 * Custom hook to access repository context functionality
 * @returns {Object} The repository context value
 */
export const useRepositoryContext = () => {
  const context = useContext(RepositoryContext);
  if (context === undefined) {
    throw new Error('useRepositoryContext must be used within a RepositoryProvider');
  }
  return context;
};
